import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import Moment from 'react-moment';
import { routes } from 'utils/routes';
import { pdfjs } from 'react-pdf';
import { PdfCanvasPage } from 'app/components/PdfCanvasPage/Loadable';
import { toast } from 'react-toast';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface QuickViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: any;
  companyId: string;
}

const getDocStatus = (status: string) => {
  switch (status) {
    case 'drafted':
      return 'In Draft';
    case 'mapped':
      return 'Pending Approval';
    case 'approved':
      return 'Pending Signature';
    case 'declined':
      return 'Declined';
    case 'binding':
      return 'Binding';
    case 'deleted':
      return 'Deleted';
    default:
      return 'Signatures Pending';
  }
};

const getPartyName = (party: any) => {
  if (!party) return 'System';
  if (typeof party === 'string') return party;
  return party.name || party.userName || party.email || 'Unknown';
};

const viewDocHistory = (entry: any) => {
  switch (entry.action) {
    case 'create':
      return 'created the document';
    case 'update':
      return 'updated the document';
    case 'sign':
      return 'signed the document';
    case 'approve':
      return 'approved the document';
    case 'decline':
      return 'declined the document';
    case 'submit':
      return 'submitted the document for review';
    case 'view':
      return 'viewed the document';
    case 'download':
      return 'downloaded the document';
    case 'share':
      return 'shared the document';
    default:
      return entry.action || 'performed an action';
  }
};

export const QuickViewModal: React.FC<QuickViewModalProps> = ({
  isOpen,
  onClose,
  document: doc,
  companyId,
}) => {
  const [pdfPages, setPdfPages] = useState<any[]>([]);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (isOpen && doc) {
      loadPdf();
    }
  }, [isOpen, doc]);

  const loadPdf = async () => {
    if (!doc?.urls) return;

    setLoadingPdf(true);
    const pdfUrl =
      doc.urls.agreementWithCertificateUrl ||
      doc.urls.agreementUrl ||
      doc.urls.draftUrl;

    if (pdfUrl) {
      try {
        const pdf = await pdfjs.getDocument(pdfUrl).promise;
        const pages: any[] = [];
        for (let i = 1; i <= Math.min(pdf.numPages, 3); i++) {
          // Load only first 3 pages for preview
          const page = await pdf.getPage(i);
          pages.push(page);
        }
        setPdfPages(pages);
      } catch (error) {
        // Handle PDF loading error silently
      }
    }
    setLoadingPdf(false);
  };

  const downloadPdf = (pdfUrl: string, fileName: string) => {
    fetch(pdfUrl)
      .then(response => response.blob())
      .then(blob => {
        const fileURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = fileName;
        link.click();
      })
      .catch(err => {
        /* Handle download error */
      });
  };

  const handleDownload = () => {
    const pdfUrl =
      doc?.urls?.agreementWithCertificateUrl ||
      doc?.urls?.agreementUrl ||
      doc?.urls?.draftUrl;
    if (pdfUrl) {
      downloadPdf(pdfUrl, `${doc?.agreement?.agreementTitle}.pdf`);
    }
  };

  const handleDownloadCertificate = () => {
    const pdfUrl = doc?.urls?.certificateUrl;
    if (pdfUrl) {
      downloadPdf(pdfUrl, `${doc?.agreement?.agreementTitle}_certificate.pdf`);
    }
  };

  const handleOpenDocument = () => {
    history.push(routes.viewCompanyDocument(companyId, doc.agrUID));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90vw',
          height: '90vh',
          maxWidth: '1200px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
              {doc?.agreement?.agreementTitle || 'Document Preview'}
            </h2>
            <div
              style={{
                marginTop: '8px',
                padding: '4px 12px',
                backgroundColor:
                  getDocStatus(doc?.agreement?.agreementStatus) === 'Binding'
                    ? '#dcfce7'
                    : '#fef3c7',
                color:
                  getDocStatus(doc?.agreement?.agreementStatus) === 'Binding'
                    ? '#166534'
                    : '#92400e',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                display: 'inline-block',
              }}
            >
              {getDocStatus(doc?.agreement?.agreementStatus)}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* PDF Viewer */}
          <div
            style={{
              flex: 2,
              padding: '20px',
              overflow: 'auto',
              backgroundColor: '#f9fafb',
            }}
          >
            {loadingPdf ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  flexDirection: 'column',
                }}
              >
                <i className="gg-spinner" style={{ fontSize: '24px' }}></i>
                <p style={{ marginTop: '16px' }}>Loading document...</p>
              </div>
            ) : pdfPages.length > 0 ? (
              <div>
                {pdfPages.map((page, index) => (
                  <div key={index} style={{ marginBottom: '20px' }}>
                    <PdfCanvasPage
                      page={page}
                      index={index + 1}
                      annotations={[]}
                      allAnnotations={[]}
                      user={null}
                      companyName=""
                      changeAnnotations={() => {}}
                      selectedParty={null}
                      selectedPartyContact={null}
                      addAnnotationBox={() => {}}
                      onDeleteAnnotationBox={() => {}}
                      isBoxesEditable={false}
                      openSignModal={() => {}}
                      docStatus={doc?.agreement?.agreementStatus}
                      signText=""
                      onSaveContents={() => {}}
                      onEditUserAnnotations={() => {}}
                      isSigningDoc={false}
                      isSignImage={false}
                      isApprover={false}
                      isInDraft={false}
                      canvasParentRef={null}
                      pdfScale={0.8}
                      isUpdating={false}
                      isImportedDoc={false}
                      requiredFieldsPending={[]}
                      isSignatureSet={false}
                      userSignBoxes={{}}
                      setSignBoxApprove={() => {}}
                      userInitialBoxes={{}}
                      setInitialBoxApprove={() => {}}
                      isSigner={-1}
                      isRecipient={-1}
                      isAdmin={false}
                      isDrafter={false}
                      allParties={[]}
                    />
                  </div>
                ))}
                {doc?.urls && (
                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <button
                      onClick={handleOpenDocument}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      View Full Document
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#6b7280',
                }}
              >
                No preview available
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div
            style={{
              flex: 1,
              borderLeft: '1px solid #e5e7eb',
              padding: '20px',
              overflow: 'auto',
            }}
          >
            {/* Document Info */}
            <div
              style={{
                marginBottom: '24px',
                paddingBottom: '20px',
                borderBottom: '1px solid var(--sidebar-border)',
              }}
            >
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px',
                }}
              >
                Document Details
              </h3>
              <div
                style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  marginBottom: '8px',
                }}
              >
                <strong>Created:</strong>{' '}
                <Moment format="MMMM DD, YYYY">{doc?.dates?.draftDate}</Moment>
              </div>
              {doc?.dates?.effectiveDate && (
                <div
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginBottom: '8px',
                  }}
                >
                  <strong>Completed:</strong>{' '}
                  <Moment format="MMMM DD, YYYY">
                    {doc?.dates?.effectiveDate}
                  </Moment>
                </div>
              )}
            </div>

            {/* Parties */}
            <div
              style={{
                marginBottom: '24px',
                paddingBottom: '20px',
                borderBottom: '1px solid var(--sidebar-border)',
              }}
            >
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px',
                }}
              >
                Parties
              </h3>
              {doc?.recipients?.map((recipient: any, index: number) => (
                <div
                  key={index}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    fontSize: '14px',
                  }}
                >
                  <div style={{ fontWeight: '500' }}>
                    {recipient.name || recipient.email}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    Recipient • {recipient.status}
                  </div>
                </div>
              ))}
              {doc?.signers?.map((signer: any, index: number) => (
                <div
                  key={index}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    fontSize: '14px',
                  }}
                >
                  <div style={{ fontWeight: '500' }}>
                    {signer.name || signer.email}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    Signer • {signer.status}
                  </div>
                </div>
              ))}
            </div>

            {/* Recipient Links */}
            {(doc?.recipients?.length > 0 || doc?.signers?.length > 0) && (
              <div
                style={{
                  marginBottom: '24px',
                  paddingBottom: '20px',
                  borderBottom: '1px solid var(--sidebar-border)',
                }}
              >
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: '12px',
                  }}
                >
                  Recipient Links
                </h3>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  Share these links with recipients to access the document
                </div>
                {[...(doc?.recipients || []), ...(doc?.signers || [])].map((recipient, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: '12px',
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <div style={{ fontWeight: '500', marginBottom: '8px', fontSize: '14px' }}>
                      {recipient.name || recipient.email}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        readOnly
                        value={
                          recipient.accessKey
                            ? window.location.host + routes.guestWelcome(doc.agrUID, recipient.accessKey)
                            : 'Link not available'
                        }
                        style={{
                          flex: 1,
                          padding: '8px',
                          fontSize: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                        }}
                      />
                      <button
                        onClick={async () => {
                          if (recipient.accessKey) {
                            try {
                              await navigator.clipboard.writeText(
                                window.location.host + routes.guestWelcome(doc.agrUID, recipient.accessKey)
                              );
                              toast.success('Link copied!');
                            } catch (err) {
                              toast.error('Failed to copy');
                            }
                          }
                        }}
                        disabled={!recipient.accessKey}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: recipient.accessKey ? '#3b82f6' : '#d1d5db',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: recipient.accessKey ? 'pointer' : 'not-allowed',
                          fontSize: '12px',
                          fontWeight: '500',
                        }}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Downloads */}
            <div
              style={{
                marginBottom: '24px',
                paddingBottom: '20px',
                borderBottom: '1px solid var(--sidebar-border)',
              }}
            >
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px',
                }}
              >
                Downloads
              </h3>
              <button
                onClick={handleDownload}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#374151',
                }}
              >
                <i className="fi fi-rr-cloud-download-alt"></i>
                Download Document
              </button>
              {doc?.urls?.certificateUrl && (
                <button
                  onClick={handleDownloadCertificate}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#374151',
                  }}
                >
                  <i className="fi fi-rr-cloud-download-alt"></i>
                  Download Certificate
                </button>
              )}
            </div>

            {/* Activity History */}
            <div
              style={{
                paddingBottom: '20px',
                borderBottom: '1px solid var(--sidebar-border)',
              }}
            >
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px',
                }}
              >
                Activity History
              </h3>
              <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                {doc?.history
                  ?.slice()
                  .reverse()
                  .map((entry: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        fontSize: '14px',
                      }}
                    >
                      <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                        {getPartyName(entry.party)} {viewDocHistory(entry)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        <Moment format="MMMM DD, YYYY @ h:mm A">
                          {entry.date}
                        </Moment>
                      </div>
                    </div>
                  ))}
                {/* Initial creation entry */}
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                >
                  <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                    {doc?.drafter?.userName || doc?.drafter?.userEmail} created
                    the document
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    <Moment format="MMMM DD, YYYY @ h:mm A">
                      {doc?.dates?.draftDate}
                    </Moment>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div style={{ marginTop: '24px' }}>
              <button
                onClick={handleOpenDocument}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                }}
              >
                Open Document
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
